import { useEffect, useState } from 'react'
import { gql } from 'graphql-request'
import { PlusIcon, ArrowLeftIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { BookOpen } from 'lucide-react'

import { useBreakpoint } from '@contexts/breakpoints'
import { useMutation } from '@hooks/graphql'
import { useUnsavedChanges } from '@contexts/unsavedChangesContext'
import { usePrompt } from '@hooks/prompt'
import { useSidebar } from '@/components/ui/sidebar'
import { Switch } from '@/components/ui/switch'
import { useUpdateEducatorProject } from '../hooks'

import FreeResponseQuestionForm from './FreeResponseQuestionForm'
import MultipleChoiceQuestionForm from './MultipleChoiceQuestionForm'
import ReflectionQuestionForm from './ReflectionQuestionForm'
import TextForm from './TextForm'
import VideoForm from './VideoForm'
import ImageForm from './ImageForm'
import AddBlockModal from './AddBlockModal'
import TutorSelectionModal from './TutorSelectionModal'
import LessonPromptModal from './LessonPromptModal'
import BlockPreview from './BlockPreview'
import NoBlockSelected from './NoBlockSelected'
import SelectedTutor from './SelectedTutor'
import NoTutorSelectedNotification from './NoTutorSelectedNotification'
import ContentSidebar from './ContentSidebar'
import FeedbackTypeSelector from './FeedbackTypeSelector'

import { Button } from '@/components/ui/button'
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable"

import { ScrollArea } from '@/components/ui/scroll-area'

const DELETE_BLOCK_MUTATION = gql`
  mutation deleteBlock($input: DeleteBlockInput!) {
    deleteBlock(input: $input) {
      success
      errors {
        message
      }
    }
  }
`

const UPDATE_BLOCK_MUTATION = gql`
  mutation UpdateBlock($input: UpdateBlockInput!) {
    updateBlock(input: $input) {
      success
      errors {
        message
      }
    }
  }
`

const UPLOAD_PUBLIC_ASSET_MUTATION = gql`
  mutation uploadPublicAsset($input: UploadPublicAssetInput!) {
    uploadPublicAsset(input: $input) {
      assetUrl
      errors {
        message
      }
    }
  }
`

const Content = ({ id, name, blocks, tutor, refetch, prompt, socraticModeEnabled, stemModeEnabled, seriesId, feedbackType }) => {
  const isMobile = useBreakpoint('md')
  const { hasUnsavedChanges, isSaving } = useUnsavedChanges()
  const { setOpen } = useSidebar()

  usePrompt(
    isSaving
      ? 'Changes are still being saved. Are you sure you want to leave?'
      : 'You have unsaved changes. Are you sure you want to leave?',
    hasUnsavedChanges || isSaving
  )

  const { updateProject, status: updateStatus } = useUpdateEducatorProject({
    id,
    onSuccess: () => {
      refetch()
    }
  })

  const [selectedBlockId, setSelectedBlockId] = useState(isMobile ? null : blocks[0]?.blockId)
  const [blockIndexToAdd, setBlockIndexToAdd] = useState(null)
  const [isTutorSelectionModalOpen, setIsTutorSelectionModalOpen] = useState(false)
  const [isLessonPromptModalOpen, setIsLessonPromptModalOpen] = useState(false)

  const [sortedBlocks, setSortedBlocks] = useState([])
  const [blockIdToDelete, setBlockIdToDelete] = useState(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require pointer to move 8px before activation
      },
    })
  )
  const { mutate: deleteBlock, isLoading: isDeleting } = useMutation({
    gqlMutation: DELETE_BLOCK_MUTATION,
    onSuccess: () => {
      refetch()
      console.log('refetched')
      if (selectedBlockId === blockIdToDelete) setSelectedBlockId(blocks[0].blockId)

      setBlockIdToDelete(null)
    }
  })
  const { mutate: updateBlock } = useMutation({
    gqlMutation: UPDATE_BLOCK_MUTATION,
    onSuccess: refetch
  })

  const handleFeedbackTypeChange = (newFeedbackType) => {
    updateProject({ feedbackType: newFeedbackType })
  }

  const onDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSortedBlocks((items) => {
        const oldIndex = items.findIndex((item) => item.blockId === active.id);
        const newIndex = items.findIndex((item) => item.blockId === over.id);

        const movedBlock = items[oldIndex];
        // Adjust for 1-based indexing on the backend
        updateBlock({ input: { blockId: movedBlock.blockId, position: newIndex + 1 } })

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  const { mutateAsync: uploadPublicAsset } = useMutation({
    gqlMutation: UPLOAD_PUBLIC_ASSET_MUTATION
  })

  const handleImageUpload = async (file) => {
    try {
      const result = await uploadPublicAsset({ input: { educatorProjectId: id, attachment: file } })
      return result.uploadPublicAsset.assetUrl
    } catch (error) {
      console.log('Error uploading image: ', error)
    }
  }

  const handleBlockSelect = (blockId) => {
    if (hasUnsavedChanges || isSaving) {
      const message = isSaving
        ? 'Changes are still being saved. Are you sure you want to switch blocks?'
        : 'You have unsaved changes. Are you sure you want to switch blocks?'

      if (!window.confirm(message)) {
        return
      }
    }
    setSelectedBlockId(blockId)
  }

  useEffect(() => {
    setSortedBlocks(blocks)
  }, [blocks, setSortedBlocks])

  const handleMobileBack = () => {
    if (hasUnsavedChanges || isSaving) {
      const message = isSaving
        ? 'Changes are still being saved. Are you sure you want to go back?'
        : 'You have unsaved changes. Are you sure you want to go back?'

      if (!window.confirm(message)) {
        return
      }
    }
    setSelectedBlockId(null)
  }

  const handleAddBlock = (index) => {
    if (hasUnsavedChanges || isSaving) {
      const message = isSaving
        ? 'Changes are still being saved. Are you sure you want to add a new block?'
        : 'You have unsaved changes. Are you sure you want to add a new block?'

      if (!window.confirm(message)) {
        return
      }
    }
    setBlockIndexToAdd(index)
  }

  const handleBlockDeleted = (deletedBlockId) => {
    if (selectedBlockId === deletedBlockId) {
      const remainingBlocks = sortedBlocks.filter(b => b.blockId !== deletedBlockId)
      setSelectedBlockId(remainingBlocks[0]?.blockId || null)
    }
  }

  const BlockList = ({ className = '' }) => (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}

    >
      <SortableContext
        items={sortedBlocks.map(b => b.blockId)}
        strategy={verticalListSortingStrategy}
      >
        <div
          className={`flex flex-col rounded-md gap-2 ${className}`}
        >
          {sortedBlocks.map((block, index) => (
            <BlockPreview
              id={block.blockId}
              key={block.blockId}
              setSelectedBlockId={handleBlockSelect}
              setBlockIdToDelete={setBlockIdToDelete}
              selected={block.blockId === selectedBlockId}
              refetch={refetch}
              onDeleted={handleBlockDeleted}
              index={index}
              {...block}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )

  return (
    <div className='flex flex-col w-full h-full'>
      <Choose>
        <When condition={isMobile}>
          <div className='flex w-full flex-col h-full'>
            <Choose>
              <When condition={!selectedBlockId}>
                <div className='p-4 overflow-y-auto bg-background h-full'>
                  <div className='flex flex-row items-center justify-start mb-4'>
                    <BookOpen className='size-5' />
                    <h1 className='text-lg font-semibold ml-3'>{name}</h1>
                  </div>

                  <h2 className='text-md font-semibold mb-2'>Tutor</h2>
                  <Choose>
                    <When condition={tutor}>
                      <SelectedTutor
                        name={tutor.name}
                        imageUrl={tutor.image?.url}
                        description={tutor.description}
                        summary={tutor.summary}
                        openModal={() => setIsTutorSelectionModalOpen(true)}
                      />
                    </When>

                    <When condition={prompt}>
                      <div className='flex items-center leading-none'>
                        <Button
                          variant="outline"
                          theme="secondary"
                          onClick={() => setIsLessonPromptModalOpen(true)}
                          className='flex group items-center'
                        >
                          Edit lesson prompt
                          <span className='ml-2 group-hover:bg-gray-200 rounded-full p-1'>
                            <PencilSquareIcon className='size-5' />
                          </span>
                        </Button>
                      </div>
                    </When>

                    <Otherwise>
                      <NoTutorSelectedNotification
                        openModal={() => setIsTutorSelectionModalOpen(true)}
                        lessonName={name}
                      />
                    </Otherwise>
                  </Choose>

                  <div className="mt-4">
                    <h2 className='text-md font-semibold mb-2'>Feedback Type</h2>
                    <FeedbackTypeSelector
                      value={feedbackType}
                      onValueChange={handleFeedbackTypeChange}
                      disabled={updateStatus === 'loading'}
                      isLoading={updateStatus === 'loading'}
                    />
                  </div>

                  <h2 className='text-md font-semibold mt-4 mb-2'>Outline</h2>
                  <BlockList className="gap-2" />

                  <Button
                    variant="outline"
                    onClick={() => handleAddBlock(sortedBlocks.length)}
                    className='mt-2 mb-8 border-dashed w-full'
                  >
                    <PlusIcon className='size-5' />
                    Add block
                  </Button>
                </div>
              </When>

              <Otherwise>
                <div className='flex flex-col h-[calc(100%-64px)] px-5  overflow-y-auto'>
                  <div className='flex flex-col gap-5 pb-10'>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleMobileBack}
                      className='w-fit mt-4'
                    >
                      <ArrowLeftIcon className='h-5 w-5 mr-2' />
                      Back
                    </Button>

                    <With block={blocks.find(block => block.blockId === selectedBlockId)}>
                      <Choose>
                        <When condition={block.__typename === 'FreeResponse'}>
                          <FreeResponseQuestionForm
                            key={block.blockId}
                            refetch={refetch}
                            uploadImage={handleImageUpload}
                            {...block}
                          />
                        </When>

                        <When condition={block.__typename === 'Question'}>
                          <MultipleChoiceQuestionForm
                            key={block.blockId}
                            refetch={refetch}
                            uploadImage={handleImageUpload}
                            {...block}
                          />
                        </When>

                        <When condition={block.__typename === 'Reflection'}>
                          <ReflectionQuestionForm
                            key={block.blockId}
                            refetch={refetch}
                            uploadImage={handleImageUpload}
                            {...block}
                          />
                        </When>

                        <When condition={block.__typename === 'Text'}>
                          <TextForm
                            key={block.blockId}
                            refetch={refetch}
                            uploadImage={handleImageUpload}
                            {...block}
                          />
                        </When>
                      </Choose>
                    </With>
                  </div>
                </div>
              </Otherwise>
            </Choose>
          </div>
        </When>

        <Otherwise>
          <ResizablePanelGroup
            className='flex grow bg-background h-[calc(100vh-64px)]'
            direction='horizontal'
            id="blockEditor"
          >
            <ResizablePanel
              className='bg-background flex flex-col w-1/4 h-[calc(100vh-64px)] overflow-y-auto'
              defaultSize={20}
              minSize={20}
            >
              <ScrollArea className='h-full p-5 pt-0 '>
                <ContentSidebar
                  name={name}
                  tutor={tutor}
                  prompt={prompt}
                  seriesId={seriesId}
                  setIsTutorSelectionModalOpen={setIsTutorSelectionModalOpen}
                  setIsLessonPromptModalOpen={setIsLessonPromptModalOpen}
                  handleAddBlock={handleAddBlock}
                  sortedBlocks={sortedBlocks}
                  BlockListComponent={BlockList}
                  setOpen={setOpen}
                  feedbackType={feedbackType}
                  onFeedbackTypeChange={handleFeedbackTypeChange}
                  isUpdatingFeedbackType={updateStatus === 'loading'}
                />
              </ScrollArea>
            </ResizablePanel>

            <ResizableHandle className='flex items-center justify-center h-full' withHandle>
            </ResizableHandle>

            <ResizablePanel
              className='flex flex-col bg-background h-[calc(100vh-64px)]'
              defaultSize={80}
              minSize={50}
            >
              <ScrollArea className='h-full px-5' type='always'>
                <div className='py-5'>
                  <With block={blocks.find(block => block.blockId === selectedBlockId)}>
                    <Choose>
                      <When condition={!block}>
                        <NoBlockSelected addBlock={() => setBlockIndexToAdd(sortedBlocks.length)} />
                      </When>

                      <When condition={block.__typename === 'FreeResponse'}>
                        <FreeResponseQuestionForm
                          key={block.blockId}
                          refetch={refetch}
                          uploadImage={handleImageUpload}
                          {...block}
                        />
                      </When>

                      <When condition={block.__typename === 'Question'}>
                        <MultipleChoiceQuestionForm
                          key={block.blockId}
                          refetch={refetch}
                          uploadImage={handleImageUpload}
                          {...block}
                        />
                      </When>

                      <When condition={block.__typename === 'Reflection'}>
                        <ReflectionQuestionForm
                          key={block.blockId}
                          refetch={refetch}
                          uploadImage={handleImageUpload}
                          {...block}
                        />
                      </When>

                      <When condition={block.__typename === 'Text'}>
                        <TextForm
                          key={block.blockId}
                          refetch={refetch}
                          uploadImage={handleImageUpload}
                          {...block}
                        />
                      </When>

                      <When condition={block.__typename === 'Video'}>
                        <VideoForm
                          key={block.blockId}
                          refetch={refetch}
                          {...block}
                        />
                      </When>

                      <When condition={block.__typename === 'Image'}>
                        <ImageForm
                          key={block.blockId}
                          refetch={refetch}
                          {...block}
                        />
                      </When>
                    </Choose>
                  </With>
                </div>
              </ScrollArea>
            </ResizablePanel>
          </ResizablePanelGroup>
        </Otherwise>
      </Choose>

      <AddBlockModal
        id={id}
        index={blockIndexToAdd}
        isOpen={blockIndexToAdd !== null}
        closeModal={() => setBlockIndexToAdd(null)}
        setSelectedBlockId={setSelectedBlockId}
        refetch={refetch}
      />

      <TutorSelectionModal
        isOpen={isTutorSelectionModalOpen}
        closeModal={() => setIsTutorSelectionModalOpen(false)}
        id={id}
        tutor={tutor}
      />

      <LessonPromptModal
        id={id}
        isOpen={isLessonPromptModalOpen}
        closeModal={() => setIsLessonPromptModalOpen(false)}
        prompt={prompt}
        socraticModeEnabled={socraticModeEnabled}
        stemModeEnabled={stemModeEnabled}
      />
    </div>
  )
}

export default Content
